import { downloadUrl } from "../../lib/api/files";
import { formatDate, formatSize } from "../../lib/format";
import { Kebab, KebabItem } from "../ui/Kebab";
import { FileIcon } from "./icons";
import type { FileDTO } from "../../lib/api/types";

interface FileRowProps {
  file: FileDTO;
  onOpenPreview: (file: FileDTO) => void;
  disabled: boolean;
}

export function FileRow({ file, onOpenPreview, disabled }: FileRowProps) {
  // Server `previewable` flag is the single source of truth.
  const name = file.previewable ? (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onOpenPreview(file)}
      className="block max-w-full truncate text-left text-body text-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:no-underline"
    >
      {file.name}
    </button>
  ) : (
    <a
      href={downloadUrl(file.id)}
      download
      aria-disabled={disabled}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
        }
      }}
      className={[
        "block max-w-full truncate text-left text-body text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        disabled ? "cursor-not-allowed" : "hover:underline",
      ].join(" ")}
    >
      {file.name}
    </a>
  );

  return (
    <div className="flex min-h-row items-center gap-3 border-b border-surface-border px-gutter py-2 hover:bg-surface-muted">
      <FileIcon mime={file.mimeType} className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        {name}
        <div className="mt-0.5 flex gap-2 text-meta text-text-muted sm:hidden">
          <span>{formatDate(file.createdAt)}</span>
          <span aria-hidden="true">·</span>
          <span>{formatSize(file.sizeBytes)}</span>
        </div>
      </div>
      <div className="hidden w-40 shrink-0 text-body text-text-muted sm:block">
        {formatDate(file.createdAt)}
      </div>
      <div className="hidden w-24 shrink-0 text-right text-body text-text-muted sm:block">
        {formatSize(file.sizeBytes)}
      </div>
      <div className="flex w-8 shrink-0 justify-end">
        <Kebab disabled={disabled} label={`Actions for ${file.name}`}>
          {(close) => (
            <KebabItem href={downloadUrl(file.id)} download onClick={close}>
              Download
            </KebabItem>
          )}
        </Kebab>
      </div>
    </div>
  );
}
